import { Z_INDEX_MAX } from '@/helpers/commonConsts'
import { commonUtils } from '@/helpers/commonUtils'
import { ComponentFactory } from '@/mobxTools'
import { userService } from '@/services/userService'
import { Modal, QRCode, Spin } from 'antd'
import classNames from 'classnames'
import { useEffect } from 'react'
import Decimal from 'decimal.js'
import DiscountCodeInput from '../DiscountCodeInput/DiscountCodeInput'
import style from './PayQrDialog.module.less'
import { PayQrDialogVm } from './PayQrDialogVm'
import { ActivityBanner } from '@/pages/PricingPage/components/VideoActivity/ActivityDesc'

/** PayQrDialog 组件的 Props */
export type PayQrDialogProps = {
  open: boolean
  plan: ServerDataTypes.FormatPlan
  source: string
  feature?: string
  from?: string
  enableDiscountCode?: boolean
  paySuccess?: (enableGiftReceiveBtn: boolean) => void
  onClose: () => void
}

const transTitle = (plan: ServerDataTypes.FormatPlan) => {
  switch (plan.timeType) {
    case 'year':
      return { title: '年度会员', unit: '年' }
    case 'month_subscription':
      return { title: '月度会员', unit: '月' }
    case 'quarter_subscription':
      return { title: '连续包季', unit: '季' }
    case 'year_subscription':
      return { title: '连续包年', unit: '年' }
    default:
      return { title: `${plan.activeTime}个月`, unit: '月' }
  }
}
/**
 * @author TODO
 * (在这里对组件进行说明)
 */
export const PayQrDialog = ComponentFactory.createComponent('PayQrDialog', PayQrDialogVm, (vm) => {
  const { open, plan, onClose, from, enableDiscountCode = false } = vm.props

  let title = ''
  if (vm.currentTabKey === 'alipay_qr' && plan.isSubscription) {
    title = plan.info.aliTitle
  } else {
    title = plan.info.wxTitle
  }

  const priceBox = () => {
    // 年
    if (plan.timeType === 'year' || plan.timeType === 'year_subscription') {
      let currentPrice = plan.price
      let monthlyPrice = plan.unitPrice

      let countDesc = ''
      if (vm.isBatchPurchase && plan.count) {
        // 使用 Decimal 进行精确计算，避免浮点数精度问题
        monthlyPrice = new Decimal(currentPrice)
          .div(new Decimal(plan.count).mul(12))
          .toDecimalPlaces(1, Decimal.ROUND_HALF_UP)
          .toNumber()
        if (plan.count > 1) {
          countDesc = `${plan.count}`
        }
      } else if (vm.discountCodePercent && plan.discountPrice) {
        currentPrice = plan.discountPrice
        // 使用 Decimal 进行精确计算，避免浮点数精度问题
        monthlyPrice = new Decimal(monthlyPrice)
          .mul(vm.discountCodePercent)
          .div(100)
          .toDecimalPlaces(1, Decimal.ROUND_HALF_UP)
          .toNumber()
      }
      return (
        <>
          <div className={classNames('flex-row', 'justify-end')}>
            <span className={style.price}>{currentPrice}</span>
            <span className={style.unit}>元/{countDesc}年</span>
            {plan.discountPercent > 0 && plan.discountPercent < 10 && !vm.isBatchPurchase && (
              <div className={style.saleIcon}>立享{plan.discountPercent}折优惠</div>
            )}
          </div>
          <div className={style.priceDesc}>
            <p className={style.currentPrice}>{monthlyPrice}元/月</p>
            <p className={style.originalPrice}>
              原价{plan.originPrice}元/{countDesc}
              {plan.unitDesc}
            </p>
          </div>
        </>
      )
    }

    // 月 或者 季
    let currentPrice =
      vm.currentTabKey === 'alipay_qr' && plan.isSubscription ? plan.subscriptionPrice : plan.price
    if (vm.discountCodePercent && plan.discountPrice) {
      currentPrice = plan.discountPrice
    }

    return (
      <>
        <div className={classNames('flex-row', 'justify-end')}>
          <span className={style.price}>{currentPrice}</span>
          <span className={style.unit}>元/{plan.unitDesc}</span>
          {plan.discountPercent > 0 && plan.discountPercent < 10 && (
            <div className={classNames(style.saleIcon, style.offset)}>
              立享{plan.discountPercent}折优惠
            </div>
          )}
        </div>
        {currentPrice !== plan.originPrice && (
          <div className={style.priceDesc}>
            <p className={style.originalPrice}>
              原价{plan.originPrice}元/{plan.unitDesc}
            </p>
          </div>
        )}
      </>
    )
  }

  return (
    <Modal
      width='572px'
      zIndex={Z_INDEX_MAX}
      getContainer={false}
      maskClosable={false}
      wrapClassName={classNames(style.payQrDialog)}
      open={open}
      centered
      footer={false}
      onCancel={() => {
        onClose()
      }}
    >
      <div
        className={classNames(style.payQrDialogContent, {
          [style.disableDiscountCode]: !enableDiscountCode,
        })}
      >
        <p className={classNames(style.title)}>
          {plan.info.prefix}-{plan.info.prefixValue}
          {plan.count && plan.count > 1 && <span className={style.countDesc}>x {plan.count}</span>}
        </p>

        {(plan.timeType === 'year' || plan.timeType === 'year_subscription') && (
          <ActivityBanner
            styleCSS={{ marginTop: '10px', maxWidth: '540px' }}
            type={`${plan.version}_${plan.timeType}` as 'max_year' | 'ultra_year'}
          />
        )}

        <div className={classNames(style.channelList)}>
          <div
            className={classNames(style.alipayBox, style.flexRow, {
              [style.active]: vm.currentTabKey === 'alipay_qr',
            })}
            onClick={() => {
              if (vm.currentTabKey === 'alipay_qr') {
                return
              }
              vm.currentTabKey = 'alipay_qr'
              vm.genOrderQrCode()
            }}
          >
            <img src='https://assets.weibanzhushou.com/web/pt-web/ali-pay.18b4779d8424.png' />
            <span>支付宝支付</span>
          </div>
          <div
            className={classNames(style.wechatBox, style.flexRow, {
              [style.active]: vm.currentTabKey === 'wx_pub_qr',
            })}
            onClick={() => {
              if (vm.currentTabKey === 'wx_pub_qr') {
                return
              }
              vm.currentTabKey = 'wx_pub_qr'
              vm.genOrderQrCode()
            }}
          >
            <img src='https://assets.weibanzhushou.com/web/pt-web/wechat-pay.9115a8ba339f.png' />
            <span>微信支付</span>
          </div>
        </div>

        {enableDiscountCode && !vm.isBatchPurchase && (
          <DiscountCodeInput
            rootClassName={style.discountCode}
            discountCode={vm.discountCodeData.discountCode}
            statusInfo={vm.discountCodeData.statusInfo}
            handleDiscountCodeChange={(value: string) => {
              vm.discountCodeData = {
                discountCode: value,
                statusInfo: {
                  status: 'none',
                  msg: '',
                },
              }
            }}
            handleDiscountCodeClick={() => {
              vm.genOrderQrCode()
            }}
          />
        )}

        <div className={classNames(style.qrBox)}>
          <img
            src='https://assets.weibanzhushou.com/web/pt-web/login-page_bg__qr.518234b9d754.png'
            className={classNames(style.qrBg)}
          />

          {vm.qrErr && (
            <div
              className={classNames(style.reloadBox)}
              onClick={() => {
                vm.genOrderQrCode()
              }}
            >
              <img
                className={classNames(style.icon)}
                src='https://assets.weibanzhushou.com/web/pt-web/icon__qr-reload.b68e5cd3c901.png'
              />
              <span>刷新</span>
            </div>
          )}
          {!vm.qrErr && (
            <Spin spinning={!vm.qrUrl || vm.qrLoading} size='small' style={{ height: '100%' }}>
              {vm.qrUrl && (
                <QRCode
                  value={vm.qrUrl}
                  size={220}
                  bordered={false}
                  style={{ padding: 0 }}
                ></QRCode>
              )}
            </Spin>
          )}
        </div>

        <div className={style.priceBox}>{vm.qrLoading ? '' : priceBox()}</div>
      </div>
    </Modal>
  )
})
